/**
 * DetailsConfirmationCard
 *
 * Step 1 of the Observatory Split journey (LUM-155 storyboard): the
 * interviewee's details as hairline ledger rows on the ivory workspace —
 * no card chrome; warm-brown small-caps labels; one navy action.
 */

import { Box, Button, Stack, Text, Title } from '@mantine/core';
import { track as breadcrumb } from '@/lib/telemetry/breadcrumb';

export interface DetailsConfirmationCardProps {
  profile: {
    full_name?: string | null;
    email?: string | null;
    organization?: string | null;
  };
  onConfirm: () => void;
}

const ROW_BORDER = '1px solid rgba(29, 36, 50, 0.16)';

function DetailRow({
  label,
  value,
}: Readonly<{ label: string; value: string }>) {
  return (
    <Box py={11} style={{ borderBottom: ROW_BORDER }}>
      <Text
        fz={10}
        fw={600}
        tt="uppercase"
        c="#99563F"
        style={{ letterSpacing: '0.18em' }}
      >
        {label}
      </Text>
      <Text fz={15} c="navy.8">
        {value}
      </Text>
    </Box>
  );
}

export function DetailsConfirmationCard({
  profile,
  onConfirm,
}: Readonly<DetailsConfirmationCardProps>) {
  const handleConfirm = () => {
    void breadcrumb('interview_details_confirmed');
    onConfirm();
  };

  return (
    <Stack gap="md">
      <Title fz={{ base: 30, sm: 36 }} fw={500} lh={1.12}>
        Confirm your details
      </Title>
      <Text size="sm" c="navy.7">
        Please check the information below is correct before we begin.
      </Text>

      <Box style={{ borderTop: ROW_BORDER }}>
        <DetailRow label="Name" value={profile.full_name || 'Not provided'} />
        <DetailRow label="Email" value={profile.email || 'Not provided'} />
        <DetailRow
          label="Organisation"
          value={profile.organization || 'Not provided'}
        />
      </Box>

      <Button size="md" w="fit-content" mt={6} onClick={handleConfirm}>
        Yes, that&apos;s me
      </Button>

      <Stack gap={4}>
        <Text size="xs" c="dimmed">
          If these details are incorrect, please contact{' '}
          <a
            href="mailto:dialogue-help@luminarium.ai"
            style={{ color: 'var(--mantine-color-navy-7)' }}
          >
            dialogue-help@luminarium.ai
          </a>{' '}
          for a new invitation link.
        </Text>
        <Text size="xs" c="dimmed">
          We collect basic usage metrics to improve service reliability. See our{' '}
          <a
            href="https://luminarium.ai/privacy"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: 'var(--mantine-color-navy-7)',
              textDecoration: 'underline',
            }}
          >
            privacy notice
          </a>
          .
        </Text>
      </Stack>
    </Stack>
  );
}
